import React from 'react';

import { useIntl } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { useFormikContext } from 'formik';
import { decode } from 'html-entities';

import { useAppSelector } from 'hooks';
import {
  greenCheck,
  personAdd,
  business,
  programs,
  shippingIcon,
  pin,
  phone,
  carePrograms
} from 'assets';
import { formatSSNForDisplay } from 'utils/Profile';
import { addressFormatterInline } from 'utils/Address';
import { InviteType, ShippingSpeed } from 'constants/enums';
import { usePrefUnitForMeasurementClass } from 'hooks';
import {
  getCarePrograms,
  getDeviceOrderings,
  getGroupByGid,
  isPrepaidOrgForGid
} from 'store/groups/reducer';
import { cartHasAmazonItem, deviceNameForSku, formatDeliveryWindow } from 'utils/Devices';

import OrderCostSummary from './OrderCostSummary';
import { IInvitation } from '../Onboard';
import DevicesList from './DevicesList';

interface CarePlanProps {
  carePlans: ICarePlanShort[];
  devices: IDeviceOrderingCatalogItem[];
  inviteType: InviteType;
  uid?: string;
  /**
   * Builds exactly the body the order will be placed with, so the cost shown is the cost charged.
   * Handed down as the builder rather than the result: this screen is the one holding the current
   * form values, and a payload assembled anywhere else could describe a different order.
   */
  buildOrderPayload?: (values: IInvitation, uid?: string) => unknown | null;
  /** Raised when the quote refuses this order, so the footer can stop it being placed. */
  onQuoteRefused?: (message: string | null) => void;
}

const Review = ({
  carePlans,
  devices,
  inviteType,
  uid,
  buildOrderPayload,
  onQuoteRefused
}: CarePlanProps) => {
  const intl = useIntl();
  const invitation = useFormikContext<IInvitation>().values;
  const storeLocale = useAppSelector((state) => state.intl.locale);
  // Keyed on the order's own group, not the sidebar selection — see the selector.
  const prepaidOrg = useAppSelector((state) => isPrepaidOrgForGid(state, invitation.gid));
  const selectedGroup = useAppSelector((state) => getGroupByGid(state, invitation.gid));
  const groupCarePrograms = useAppSelector((state) => getCarePrograms(state, invitation.gid));
  const locale = invitation?.patientInfo?.locale;
  const currentLanguage = useAppSelector((state) => locale && state.intl.languages[locale]);
  const heightUnit = usePrefUnitForMeasurementClass('height');
  const weightUnit = usePrefUnitForMeasurementClass('weight');
  const catalog = useAppSelector(getDeviceOrderings);
  const showShippingSpeed = cartHasAmazonItem(
    { ...invitation.devices, ...(invitation.accessories || {}) },
    catalog
  );
  const shippingSpeedWindow = invitation.shippingSpeedEstimate
    ? formatDeliveryWindow(
        invitation.shippingSpeedEstimate.start,
        invitation.shippingSpeedEstimate.end,
        storeLocale
      )
    : null;

  const getOtherMedicalConditions = () => {
    if (!invitation.medicalInfo.conditions?.length) return '-';
    return invitation.medicalInfo.conditions.map((condition) => condition.label).join(', ');
  };

  const showPatientSection =
    inviteType !== InviteType.BULK_ORDER_DEVICES &&
    inviteType !== InviteType.BULK_ORDER_ACCESSORIES;

  return (
    <>
      <h4 className="mb-4" data-test="confirmation-title">
        {intl.formatMessage({ id: 'confirmationKey' })}
      </h4>
      {/*
        Prepaid organisations only. Devices are drawn from a credit balance on that model and
        nowhere else, so on any other group the summary announced a cost the practice does not pay
        and a shortfall against a balance it does not have — "this order needs $95.00 more than you
        have available", on an order nothing was going to stop (Tatiana, 2026-09-15). It also spared
        every non-DTP order review a quote request that was never going to be shown.

        Keyed on `invitation.gid`. The footer uses `isPrepaidOrg`, which reads the *sidebar*
        selection — with "All groups" chosen, or for a clinician whose access is subgroups only,
        that resolves to "not prepaid" for an organisation that is, and the price disappears from
        an order the credit gate will still price and can still refuse.
      */}
      {prepaidOrg && (
        <OrderCostSummary
          payload={buildOrderPayload ? buildOrderPayload(invitation, uid) : null}
          locale={storeLocale}
          onRefused={onQuoteRefused}
          deviceName={(sku) => deviceNameForSku(intl, catalog, sku)}
        />
      )}
      <div
        className="d-flex align-items-center p-4 bg-blue-light mb-4 br-5"
        data-test="review-disclaimer"
      >
        <img src={greenCheck} />
        <p className="m-0 ms-3">{intl.formatMessage({ id: 'confirmInformationKey' })}</p>
      </div>

      <Row className="mt-5">
        <Col sm={8} className="d-flex align-items-start" data-test="review-group">
          <img
            src={business}
            alt={intl.formatMessage({ id: 'businessIconKey' })}
            className="me-2"
          />
          <div>
            <h6 data-test="review-group-title">{intl.formatMessage({ id: 'groupLabel' })}</h6>
            <p data-test="review-group-name">{selectedGroup?.name}</p>
          </div>
        </Col>
      </Row>

      {showPatientSection && (
        <>
          <Row className="mt-5 justify-content-between">
            <Col sm={8} className="d-flex align-items-start" data-test="review-patient">
              <img
                src={personAdd}
                alt={intl.formatMessage({ id: 'addAPersonIconKey' })}
                className="me-2 align-self-start"
              />
              <div>
                <h6 data-test="review-patient-title">{intl.formatMessage({ id: 'patientKey' })}</h6>
                <div className="d-flex">
                  <div>
                    <p className="m-0" data-test="review-patient-name">
                      {intl.formatMessage({ id: 'name' })}: {invitation.patientInfo.lastName},{' '}
                      {invitation.patientInfo.firstName}
                    </p>
                    <p className="m-0" data-test="review-patient-username">
                      {intl.formatMessage({ id: 'usernameKey' })}: {invitation.patientInfo.username}
                    </p>
                    {invitation.patientInfo.ssn && (
                      <p className="m-0" data-test="review-patient-ssn">
                        {intl.formatMessage({ id: 'ssnKey' })}:{' '}
                        {formatSSNForDisplay(invitation.patientInfo.ssn)}
                      </p>
                    )}
                    <p className="m-0" data-test="review-patient-bday">
                      {intl.formatMessage({ id: 'dobLongKey' })}:{' '}
                      {invitation.patientInfo.bday &&
                        DateTime.fromISO(invitation.patientInfo.bday)
                          .setLocale(storeLocale)
                          .toLocaleString(DateTime.DATE_SHORT)}
                    </p>
                  </div>
                  <div className="ms-5">
                    <p
                      className="m-0"
                      data-test="review-patient-gender"
                      data-test-value={invitation.patientInfo.gender}
                    >
                      {intl.formatMessage({ id: 'Gender' })}:{' '}
                      {intl.formatMessage({
                        id: invitation.patientInfo.gender
                      })}
                    </p>
                    {invitation.patientInfo.mrn && (
                      <p className="m-0" data-test="review-patient-mrn">
                        {intl.formatMessage({ id: 'alternateIdKey' })}: {invitation.patientInfo.mrn}
                      </p>
                    )}
                    <p
                      className="m-0"
                      data-test="review-patient-language"
                      data-test-value={invitation.patientInfo.locale}
                    >
                      {intl.formatMessage({ id: 'languageTitle' })}: {currentLanguage}
                    </p>
                    {invitation.medicalInfo.provider && (
                      <p className="m-0" data-test="review-patient-provider">
                        {intl.formatMessage({ id: 'doctor_in_charge' })}:{' '}
                        {invitation.medicalInfo.provider}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Col>
            {!uid && (
              <Col sm={4} className="d-flex align-items-start" data-test="review-careplan">
                <img
                  src={programs}
                  alt={intl.formatMessage({ id: 'carePlansIconKey' })}
                  className="me-2 align-self-start"
                />
                <div>
                  <h6 data-test="review-careplan-title">
                    {intl.formatMessage({ id: 'carePlansKey' })}
                  </h6>
                  <div data-test="review-careplan-list">
                    {invitation.careplanTemplateIds.length ? (
                      invitation.careplanTemplateIds.map((id, key) => {
                        const carePlan = carePlans.find((carePlan) => carePlan.template_id === id);
                        return (
                          <p
                            key={key}
                            className="mb-0"
                            data-test={`review-careplan-${id}`}
                            data-test-value={id}
                          >
                            {intl.formatMessage({ id: decode(carePlan?.template_description) }) ||
                              decode(carePlan?.template_description)}
                          </p>
                        );
                      })
                    ) : (
                      <p data-test="review-careplan-empty">
                        {intl.formatMessage({ id: 'NothingSelected' })}
                      </p>
                    )}
                  </div>
                </div>
              </Col>
            )}
          </Row>

          <Row className="mt-5 mb-3">
            <Col sm={8} className="d-flex align-items-start" data-test="review-contact">
              <div className="review-img-wrapper">
                <img
                  src={phone}
                  alt={intl.formatMessage({ id: 'phoneIconKey' })}
                  className="me-2"
                />
              </div>
              <div>
                <h6 data-test="review-contact-title">
                  {intl.formatMessage({ id: 'contactInfoKey' })}
                </h6>
                <p className="m-0" data-test="review-contact-mobile">
                  {intl.formatMessage({ id: 'mobileNumber' })}:{' '}
                  {invitation.patientInfo.mobileNumber
                    ? invitation.patientInfo.mobileNumber
                    : intl.formatMessage({ id: 'noneKey' })}
                </p>
                <p className="m-0" data-test="review-contact-phone">
                  {intl.formatMessage({ id: 'otherPhoneNumber' })}:{' '}
                  {invitation.patientInfo.phoneNumber
                    ? invitation.patientInfo.phoneNumber
                    : intl.formatMessage({ id: 'noneKey' })}
                </p>
                <p className="m-0" data-test="review-contact-email">
                  {intl.formatMessage({ id: 'emailKey' })}:{' '}
                  {invitation.patientInfo.email_address
                    ? invitation.patientInfo.email_address
                    : intl.formatMessage({ id: 'noneKey' })}
                </p>
              </div>
            </Col>
            {inviteType !== InviteType.ORDER_PATIENT_DEVICE && (
              <Col sm={4} className="d-flex align-items-start" data-test="review-programs">
                <img
                  src={carePrograms}
                  alt={intl.formatMessage({ id: 'carePlansIconKey' })}
                  className="me-2 align-self-start"
                />
                <div>
                  <h6 data-test="review-programs-title">
                    {intl.formatMessage({ id: 'programsKey' })}
                  </h6>
                  <div data-test="review-programs-list">
                    {invitation.patientCareProgram.length ? (
                      invitation.patientCareProgram.map((cpid, key) => {
                        const careProgram = groupCarePrograms.find(
                          (program) => program.cpid === cpid
                        );
                        return (
                          <p key={key} className="mb-0" data-test-value={careProgram?.cpid}>
                            {intl.formatMessage({ id: `${careProgram?.name}_short` })}
                          </p>
                        );
                      })
                    ) : (
                      <p data-test="review-programs-empty">
                        {intl.formatMessage({ id: 'NothingSelected' })}
                      </p>
                    )}
                  </div>
                </div>
              </Col>
            )}
          </Row>
          <Row className="mt-5 mb-3">
            <Col className="d-flex align-items-start" data-test="review-medical-info">
              <img
                src={personAdd}
                alt={intl.formatMessage({ id: 'addAPersonIconKey' })}
                className="me-2 align-self-start"
              />
              <div>
                <h6 data-test="review-medical-info-title">
                  {intl.formatMessage({ id: 'medicalInformationKey' })}
                </h6>
                <p className="m-0" data-test="review-medical-info-primary">{`${intl.formatMessage({
                  id: 'primaryConditionKey'
                })}: ${
                  invitation.medicalInfo.primaryRpmReason
                    ? intl.formatMessage({
                        id: invitation.medicalInfo.primaryRpmReason
                      })
                    : '-'
                }`}</p>
                <p className="m-0" data-test="review-medical-info-secondary">{`${intl.formatMessage(
                  {
                    id: 'secondaryConditionKey'
                  }
                )}: ${
                  invitation.medicalInfo.secondaryRpmReason
                    ? intl.formatMessage({ id: invitation.medicalInfo.secondaryRpmReason })
                    : '-'
                }`}</p>
                <p
                  className="m-0"
                  data-test="review-medical-info-other-conditions"
                >{`${intl.formatMessage({
                  id: 'otherConditionsKey'
                })}: ${getOtherMedicalConditions()}`}</p>
                <p
                  className="m-0"
                  data-test="review-medical-info-other-info"
                >{`${intl.formatMessage({ id: 'otherMedicalInfoKey' })}: ${
                  invitation.medicalInfo.otherMedicalInfo
                }`}</p>
                <p
                  className="m-0"
                  data-test="review-medical-info-provider-name"
                >{`${intl.formatMessage({
                  id: 'doctor_in_charge'
                })}: ${invitation.medicalInfo.provider || '-'}`}</p>
                <p className="m-0" data-test="review-medical-info-provider">{`${intl.formatMessage({
                  id: 'coverageKey'
                })}: ${invitation.medicalInfo.payer}`}</p>
                {(invitation.medicalInfo.height ||
                  invitation.medicalInfo.weight ||
                  invitation.medicalInfo.dryWeight) && (
                  <>
                    <p className="m-0 mt-2 fw-semibold">
                      {intl.formatMessage({ id: 'baselineMeasurementsKey' })}
                    </p>
                    {invitation.medicalInfo.height && (
                      <p
                        className="m-0"
                        data-test="review-medical-info-height"
                      >{`${intl.formatMessage({ id: 'heightKey' })}: ${
                        heightUnit === 'ft'
                          ? `${invitation.medicalInfo.height} ft ${invitation.medicalInfo.heightInches || '0'} in`
                          : `${invitation.medicalInfo.height} ${heightUnit}`
                      }`}</p>
                    )}
                    {invitation.medicalInfo.weight && (
                      <p
                        className="m-0"
                        data-test="review-medical-info-weight"
                      >{`${intl.formatMessage({ id: 'weightKey' })}: ${invitation.medicalInfo.weight} ${weightUnit}`}</p>
                    )}
                    {invitation.medicalInfo.dryWeight && (
                      <p
                        className="m-0"
                        data-test="review-medical-info-dry-weight"
                      >{`${intl.formatMessage({ id: 'dryWeightKey' })}: ${invitation.medicalInfo.dryWeight} ${weightUnit}`}</p>
                    )}
                  </>
                )}
              </div>
            </Col>
          </Row>
        </>
      )}
      {(Object.keys(invitation.devices).length > 0 ||
        Object.keys(invitation.accessories || {}).length > 0) && (
        <>
          <div data-test="review-devices">
            <Row className="mt-5 mb-3">
              <Col className="d-flex align-items-start">
                <img
                  src={shippingIcon}
                  alt={intl.formatMessage({ id: 'shoppingCartIconKey' })}
                  className="me-2"
                />
                <div>
                  <h6 data-test="review-devices-title">{intl.formatMessage({ id: 'order' })}</h6>
                </div>
              </Col>
            </Row>
            <DevicesList
              selectedDevices={{ ...invitation.devices, ...invitation.accessories }}
              devices={devices}
              displayQuantity={
                inviteType === InviteType.BULK_ORDER_DEVICES ||
                inviteType === InviteType.BULK_ORDER_ACCESSORIES
              }
              dataTestId="review-devices-list"
            />
          </div>
        </>
      )}
      {invitation.shippingInfo.address.city.length > 0 && (
        <>
          <Row className="mt-5 mb-3">
            <Col sm={4} className="d-flex align-items-start" data-test="review-shipping">
              <div className="review-img-wrapper">
                <img src={pin} className="me-2" />
              </div>
              <div>
                <h6 data-test="review-shipping-title">
                  {intl.formatMessage({ id: 'shippingAddressKey' })}
                </h6>
                <p className="m-0" data-test="review-shipping-name">
                  {invitation.shippingInfo.lastName + ', ' + invitation.shippingInfo.firstName}
                </p>
                {invitation.shippingInfo.email && invitation.shippingInfo.email.length > 0 && (
                  <p className="m-0" data-test="review-shipping-email">
                    {invitation.shippingInfo.email}
                  </p>
                )}
                {invitation.shippingInfo.phoneNumber &&
                  invitation.shippingInfo.phoneNumber.length > 0 && (
                    <p className="m-0" data-test="review-shipping-phone">
                      {invitation.shippingInfo.phoneNumber}
                    </p>
                  )}
                {invitation.shippingInfo.address.company &&
                  invitation.shippingInfo.address.company.length > 0 && (
                    <p className="m-0" data-test="review-shipping-company">
                      {invitation.shippingInfo.address.company}
                    </p>
                  )}
                <p className="m-0" data-test="review-shipping-address">
                  {invitation.shippingInfo.address
                    ? addressFormatterInline(invitation.shippingInfo.address, true)
                    : ''}
                </p>
                {showShippingSpeed && (
                  <p
                    className="m-0 mt-2"
                    data-test="review-shipping-speed"
                    data-test-value={invitation.shippingSpeed}
                  >
                    {intl.formatMessage({ id: 'devices.shippingSpeed.title' })}:{' '}
                    {intl.formatMessage({
                      id:
                        invitation.shippingSpeed === ShippingSpeed.EXPEDITED
                          ? 'devices.shippingSpeed.expedited'
                          : 'devices.shippingSpeed.standard'
                    })}
                    {shippingSpeedWindow && (
                      <>
                        {' · '}
                        {intl.formatMessage(
                          { id: 'devices.shippingSpeed.arrives' },
                          { window: shippingSpeedWindow }
                        )}
                      </>
                    )}
                  </p>
                )}
              </div>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default Review;
